import React, { useEffect } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import StandaloneSection from '../shared/StandaloneSection';
import { TaxYearSelector } from './TaxYearSelector';
import {
  useAutoRefreshTrigger,
  useGlobalAutoRefreshStore,
} from '../../stores/useGlobalAutoRefreshStore';
import { PortfoliosView } from './PortfoliosView';
import { CoinbaseTransactionsView } from './CoinbaseTransactionsView';
import { SystemTradesView } from './SystemTradesView';
import { ReconciliationView } from './ReconciliationView';
import { TaxLotsView } from './TaxLotsView';
import { CostBasisView } from './CostBasisView';
import { MissingCostBasisView } from './MissingCostBasisView';
import ManualTransactionsView from './ManualTransactionsView';
import ManualTaxLotsView from './ManualTaxLotsView';
import CoinbaseCorrectionsView from './CoinbaseCorrectionsView';
import PipelineRunsView from './PipelineRunsView';
import './TaxReportingSection.css';

const SUB_NAV: { path: string; label: string; element: React.ReactElement }[] = [
  { path: 'portfolios', label: 'Overview', element: <PortfoliosView /> },
  { path: 'coinbase-transactions', label: 'Coinbase Transactions', element: <CoinbaseTransactionsView /> },
  { path: 'system-trades', label: 'System Trades', element: <SystemTradesView /> },
  { path: 'reconciliation', label: 'Reconciliation', element: <ReconciliationView /> },
  { path: 'tax-lots', label: 'Tax Lots', element: <TaxLotsView /> },
  { path: 'cost-basis', label: 'Cost Basis', element: <CostBasisView /> },
  { path: 'missing-cost-basis', label: 'Missing Cost Basis', element: <MissingCostBasisView /> },
  { path: 'manual-transactions', label: 'Manual Transactions', element: <ManualTransactionsView /> },
  { path: 'manual-tax-lots', label: 'Manual Tax Lots', element: <ManualTaxLotsView /> },
  { path: 'coinbase-corrections', label: 'Coinbase Corrections', element: <CoinbaseCorrectionsView /> },
  { path: 'pipeline/runs', label: 'Pipeline Runs', element: <PipelineRunsView /> },
];

/**
 * TaxReportingSection
 *
 * Top-level shell for the Tax Reporting area:
 * - Persistent header with the TaxYearSelector
 * - Sub-navigation tabs across the seven views
 * - Nested routes
 * - Auto-refresh registration as the 'tax-reporting' dashboard
 *
 * The tax-year default lives in useTaxYearStore (prior calendar year);
 * the selector is data-agnostic so a future-year run from CTR-014 is
 * possible. Sub-views render their own tables inside
 * `tax-reporting-section__content`.
 */
export const TaxReportingSection: React.FC = () => {
  const setActiveDashboard = useGlobalAutoRefreshStore((s) => s.setActiveDashboard);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');

  // Used by sub-views to refetch on auto-refresh.
  void refreshTrigger;

  useEffect(() => {
    setActiveDashboard('tax-reporting');
    return () => setActiveDashboard(null);
  }, [setActiveDashboard]);

  return (
    <StandaloneSection title="TAX REPORTING">
      <div className="tax-reporting-section">
        <header className="tax-reporting-section__header">
          <TaxYearSelector />
        </header>

        <nav className="tax-reporting-section__subnav" aria-label="Tax Reporting Sub-navigation">
          {SUB_NAV.map((item) => (
            <NavLink
              key={item.path}
              to={`/tax-reporting/${item.path}`}
              className={({ isActive }) =>
                `tax-reporting-section__subnav-link${isActive ? ' tax-reporting-section__subnav-link--active' : ''}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <main className="tax-reporting-section__content">
          <Routes>
            <Route index element={<Navigate to="/tax-reporting/portfolios" replace />} />
            {SUB_NAV.map((item) => (
              <Route key={item.path} path={item.path} element={item.element} />
            ))}
          </Routes>
        </main>
      </div>
    </StandaloneSection>
  );
};

export default TaxReportingSection;
